import { CheckIcon, CircleInfoIcon, PlusIcon } from "@xola/icons";
import { Label, Tooltip } from "@xola/ui-kit";
import { isEquipmentBased, isScheduled } from "common";
import { produce } from "immer";
import React, { ReactNode, useState } from "react";
import {
    AllowedPrivacy,
    DemographicPricingGroup,
    Experience,
    LineItemDestination,
    LineItemTemplate,
    LineItemTemplateCode,
    LineItemTemplateObject,
    LineItemType,
    LineItemVisibility,
    Package,
    PricingGroupSchedules,
    PricingStructure,
    PricingTerm,
    PricingTermType,
    PricingType,
} from "types";
import { getScheduleSummary } from "../../../helpers/scheduleSummaryHelper";
import { useAutoFetchCollection } from "../../../hooks/useAutoFetchCollection";
import { hasTerms } from "../../../models/DemographicPricingGroup";
import { updateQuantityInPricingTerms } from "../../../models/DemographicPricingTerm";
import { isOptionallyPrivateExperience } from "../../../models/Experience";
import { getPackageIfExperienceExists } from "../../../models/Package";
import { NoTranslate } from "../../Common/NoTranslate";
import { RadioInput } from "../../Common/RadioInput";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { AddSchedulePopover } from "./AddSchedulePopover";
import { BuyoutPricingEditor } from "./BuyoutPricingEditor";
import { EditTieredPricingModal } from "./EditTieredPricingModal";
import { FlatPricingEditor, FlatPricingPerOutingEditor } from "./FlatPricingEditor";
import { RelativePricingEditor } from "./RelativePricingEditor";
import { TieredPricingEditor } from "./TieredPricingEditor";
import { api } from "@/api/api";
import { useUser } from "@/hooks/useAccount";
import { useInvalidate } from "@/hooks/useInvalidation";
import { generateBuyoutPricingDemographicPrices } from "@/models/BuyoutPricingGroup";
import { getDemographicPricesForExperienceCatalog } from "@/models/DemographicPrice";
import {
    generateBuyoutPricingEquipmentPrices,
    generateRelativePricingEquipmentPrices,
    getEquipmentPricesForExperienceCatalog,
} from "@/models/EquipmentPrice";
import { getLineItemTemplateByCode } from "@/models/LineItemTemplate";
import { isBuyoutPricing } from "@/models/PricingGroup";
import { generateRelativePricingDemographicPrices } from "@/models/RelativePricingGroup";

interface PricingEditorProps {
    privacy: AllowedPrivacy;
    experience: Experience;
    lineItemTemplates: LineItemTemplate[];
    errors?: any;
    pricingGroup: DemographicPricingGroup;
    hasTieredPublicPricing?: boolean;
    onChange: (key: string, value: any) => void;
}

export const PricingEditor = ({
    privacy,
    experience,
    lineItemTemplates,
    errors,
    pricingGroup,
    hasTieredPublicPricing: isTieredPricing,
    onChange,
}: PricingEditorProps) => {
    const { allowedPrivacies } = experience;
    const isOptionallyPrivate = isOptionallyPrivateExperience(experience);
    const isEquipment = isEquipmentBased(experience);
    const user = useUser();
    const invalidate = useInvalidate();
    // Show relative pricing only if the experience in optionally private and per person pricing.
    const shouldShowRelativePricing =
        isOptionallyPrivate &&
        privacy === AllowedPrivacy.PRIVATE &&
        pricingGroup.pricingType === PricingType.PER_PERSON;

    const shouldShowBuyoutPricing =
        isOptionallyPrivate &&
        privacy === AllowedPrivacy.PRIVATE &&
        pricingGroup.pricingType === PricingType.PER_PERSON;

    const shouldDisableBuyoutPricing = Boolean(!(experience.group?.outingMax && !isTieredPricing));

    const isExperienceScheduled = isScheduled(experience);
    const shouldShowPricingType =
        privacy === AllowedPrivacy.PRIVATE || (privacy === AllowedPrivacy.PUBLIC && !isExperienceScheduled);

    const { data: packages } = useAutoFetchCollection<Package>("packages");
    const packageDetails = packages && getPackageIfExperienceExists(packages, experience.id);

    const handleScheduleSubmit = (value: PricingGroupSchedules) => {
        onChange("schedules", value);
    };

    const [showEditTieredPricingModal, setShowEditTieredPricingModal] = useState(false);

    const toggleEditTieredPricingModal = () => {
        setShowEditTieredPricingModal(!showEditTieredPricingModal);
    };

    const ensurePrivateUpgradeTemplateExists = async () => {
        const privateUpgradeTemplate = getLineItemTemplateByCode(
            lineItemTemplates,
            LineItemTemplateCode.PRIVATE_UPGRADE,
        );
        if (!privateUpgradeTemplate) {
            const payload: any = {
                code: LineItemTemplateCode.PRIVATE_UPGRADE,
                name: "Private Upgrade",
                destination: LineItemDestination.SELLER,
                visible: true,
                visibility: LineItemVisibility.ALL,
                system: true,
                includeInAmount: true,
                seller: { id: experience.seller.id },
                createdBy: { id: user.id },
                type: LineItemType.FEEABLE_TAXABLE_ADJUSTMENT,
                object: LineItemTemplateObject.DEFAULT,
            };

            api.post<LineItemTemplate>(`/lineItemTemplates`, payload).then(() => {
                invalidate("lineItemTemplates");
            });
        }
    };

    const handlePricingStructureChange = async (value: PricingStructure) => {
        const currentPricingStructure = pricingGroup.pricingStructure;
        onChange("pricingStructure", value);

        switch (value) {
            case PricingStructure.FLAT:
            case PricingStructure.TIERED: {
                const currentPricingGroup = { ...pricingGroup };
                const isExistingStructureRelative = currentPricingStructure === PricingStructure.RELATIVE;
                const updatedPricingGroup = produce(currentPricingGroup, (draft) => {
                    if (isExistingStructureRelative) {
                        // Reset the terms when we change from relative to flat / tiered
                        draft.terms = [
                            {
                                demographicPrices: isEquipment
                                    ? getEquipmentPricesForExperienceCatalog(experience, {
                                          demographicPrices: [],
                                          type: PricingTermType.BASE,
                                      })
                                    : getDemographicPricesForExperienceCatalog(experience, {
                                          demographicPrices: [],
                                          type: PricingTermType.BASE,
                                      }),
                                type: PricingTermType.BASE,
                            },
                        ];
                    } else {
                        draft.terms =
                            value === PricingStructure.FLAT ? draft.terms : updateQuantityInPricingTerms(draft.terms);
                    }
                });
                onChange("terms", updatedPricingGroup.terms);

                break;
            }

            case PricingStructure.RELATIVE: {
                const currentPricingGroup = { ...pricingGroup };
                const relativeDemographicPrices = isEquipment
                    ? generateRelativePricingEquipmentPrices(experience.catalog.items)
                    : generateRelativePricingDemographicPrices(experience.catalog.items);
                // If pricing Group has Relative pricing then apply the same values, else start from beginning
                const updatedPricingGroup = produce(currentPricingGroup, (draft) => {
                    const pricingTerm: PricingTerm = {
                        demographicPrices: relativeDemographicPrices,
                        type: PricingTermType.ADJUSTMENT,
                    };
                    draft.terms = [pricingTerm];
                });
                onChange("terms", updatedPricingGroup.terms);

                break;
            }

            case PricingStructure.BUYOUT: {
                await ensurePrivateUpgradeTemplateExists();
                const currentPricingGroup = { ...pricingGroup };
                const updatedPricingGroup = produce(currentPricingGroup, (draft) => {
                    draft.terms = [
                        {
                            demographicPrices: isEquipment
                                ? generateBuyoutPricingEquipmentPrices(experience.catalog.items, lineItemTemplates)
                                : generateBuyoutPricingDemographicPrices(experience.catalog.items, lineItemTemplates),
                            type: PricingTermType.ADJUSTMENT,
                        },
                    ];
                });
                onChange("terms", updatedPricingGroup.terms);
                break;
            }
            // No default
        }
    };

    const handlePricingTypeChange = (value: PricingType) => {
        onChange("pricingType", value);
        // If per person is changed to per event, then change pricing structure to flat pricing
        if (
            value === PricingType.PER_OUTING &&
            [PricingStructure.RELATIVE, PricingStructure.BUYOUT].includes(pricingGroup.pricingStructure)
        ) {
            onChange("pricingStructure", PricingStructure.FLAT);
        }
    };

    const handleUpdateTier = (tierIndex: number, pricingTerm: PricingTerm) => {
        const currentPricingGroup = { ...pricingGroup };
        const updatedPricingGroup = produce(currentPricingGroup, (draft) => {
            if (tierIndex < draft.terms.length) {
                draft.terms[tierIndex] = { ...pricingTerm };
            } else {
                draft.terms.push({ ...pricingTerm });
            }

            draft.terms = updateQuantityInPricingTerms(draft.terms);
        });
        onChange("terms", updatedPricingGroup.terms);
        toggleEditTieredPricingModal();
    };

    if (isTieredPricing && isBuyoutPricing(pricingGroup)) {
        handlePricingStructureChange(PricingStructure.FLAT);
    }

    let tooltipContent = "";
    if (shouldDisableBuyoutPricing) {
        tooltipContent = isTieredPricing
            ? "This option is not supported with Tiered Pricing."
            : `The "Per Event Limit" must have a limit for this option to be available. You can configure this in the "Schedules & Availability" section.`;
    }

    return (
        <div className="space-y-4">
            {shouldShowPricingType ? (
                <div className="space-y-2">
                    <Label>Pricing Type</Label>
                    <RadioInput
                        checked={pricingGroup.pricingType === PricingType.PER_PERSON}
                        name={`${pricingGroup.privacy}_pricingType`}
                        className="text-gray-darker"
                        onChange={() => handlePricingTypeChange(PricingType.PER_PERSON)}
                    >
                        {isEquipment ? "Per Equipment" : "Per Person"}
                    </RadioInput>
                    <RadioInput
                        checked={pricingGroup.pricingType === PricingType.PER_OUTING}
                        name={`${pricingGroup.privacy}_pricingType`}
                        className="text-gray-darker"
                        disabled={!!packageDetails}
                        onChange={() => handlePricingTypeChange(PricingType.PER_OUTING)}
                    >
                        Per Event
                        {!!packageDetails && (
                            <Tooltip
                                content={
                                    <>
                                        Cannot change price type to Per Event since this experience is part of package
                                        &quot;
                                        <var data-var="packageName">{packageDetails.name}</var>&quot;. Remove this
                                        experience from package and try again.
                                    </>
                                }
                            >
                                <CircleInfoIcon className="ml-1" />
                            </Tooltip>
                        )}
                    </RadioInput>
                </div>
            ) : null}

            <div className="w-fit space-y-2">
                <Label>Pricing Structure</Label>
                <RadioInput
                    checked={pricingGroup.pricingStructure === PricingStructure.FLAT}
                    name={`${pricingGroup.privacy}_pricingStructure`}
                    className="text-gray-darker"
                    onChange={async () => handlePricingStructureChange(PricingStructure.FLAT)}
                >
                    Flat Pricing
                </RadioInput>
                {shouldShowRelativePricing ? (
                    <RadioInput
                        checked={pricingGroup.pricingStructure === PricingStructure.RELATIVE}
                        name={`${pricingGroup.privacy}_pricingStructure`}
                        className="text-gray-darker"
                        onChange={async () => handlePricingStructureChange(PricingStructure.RELATIVE)}
                    >
                        Relative Pricing
                    </RadioInput>
                ) : null}
                <RadioInput
                    checked={pricingGroup.pricingStructure === PricingStructure.TIERED}
                    name={`${pricingGroup.privacy}_pricingStructure`}
                    className="text-gray-darker"
                    onChange={async () => handlePricingStructureChange(PricingStructure.TIERED)}
                >
                    Tiered Pricing
                </RadioInput>
                {shouldShowBuyoutPricing ? (
                    <Tooltip content={tooltipContent} disabled={!shouldDisableBuyoutPricing} placement="right">
                        <RadioInput
                            checked={isBuyoutPricing(pricingGroup)}
                            name={`${pricingGroup.privacy}_pricingStructure`}
                            className="mt-2 text-gray-darker"
                            disabled={shouldDisableBuyoutPricing}
                            onChange={async () => handlePricingStructureChange(PricingStructure.BUYOUT)}
                        >
                            Buyout remaining spots
                        </RadioInput>
                    </Tooltip>
                ) : null}
            </div>
            <div className="space-y-1" data-testid="pricing-editor-terms">
                <div className="flex items-center justify-between">
                    <Label name={`${privacy}Pricing.terms`} className={errors?.terms ? "!text-danger" : ""}>
                        {pricingGroup.pricingType === PricingType.PER_OUTING &&
                        pricingGroup.pricingStructure === PricingStructure.FLAT
                            ? "Price per event "
                            : "Terms "}
                        {errors?.terms}
                    </Label>
                    {pricingGroup.pricingStructure === PricingStructure.TIERED ? (
                        <SecondaryButton size="small" icon={<PlusIcon />} onClick={toggleEditTieredPricingModal}>
                            Add Tier
                        </SecondaryButton>
                    ) : null}
                </div>
                <div className="items-center space-y-1.5">
                    {pricingGroup.pricingStructure === PricingStructure.FLAT ? (
                        pricingGroup.pricingType === PricingType.PER_PERSON ? (
                            <FlatPricingEditor
                                experience={experience}
                                pricingGroup={pricingGroup}
                                onSubmit={(terms: PricingTerm[]) => onChange("terms", terms)}
                            />
                        ) : (
                            <FlatPricingPerOutingEditor
                                lineItemTemplates={lineItemTemplates}
                                pricingGroup={pricingGroup}
                                onChange={(terms: PricingTerm[]) => onChange("terms", terms)}
                            />
                        )
                    ) : null}

                    {pricingGroup.pricingStructure === PricingStructure.RELATIVE ? (
                        <RelativePricingEditor
                            error={errors}
                            pricingGroup={pricingGroup}
                            onChange={(terms: PricingTerm[]) => onChange("terms", terms)}
                        />
                    ) : null}

                    {pricingGroup.pricingStructure === PricingStructure.TIERED ? (
                        <>
                            {hasTerms(pricingGroup) ? (
                                <TieredPricingEditor
                                    lineItemTemplates={lineItemTemplates}
                                    experience={experience}
                                    pricingGroup={pricingGroup}
                                    onSubmit={(terms: PricingTerm[]) => onChange("terms", terms)}
                                />
                            ) : null}
                            <EditTieredPricingModal
                                lineItemTemplates={lineItemTemplates}
                                isOpen={showEditTieredPricingModal}
                                tierIndex={hasTerms(pricingGroup) ? pricingGroup.terms.length : 0}
                                experience={experience}
                                pricingGroup={pricingGroup}
                                onClose={() => setShowEditTieredPricingModal(false)}
                                onDelete={() => null}
                                onSubmit={handleUpdateTier}
                            />
                        </>
                    ) : null}

                    {isBuyoutPricing(pricingGroup) ? (
                        <BuyoutPricingEditor
                            error={errors}
                            pricingGroup={pricingGroup}
                            onChange={(terms: PricingTerm[]) => onChange("terms", terms)}
                        />
                    ) : null}
                </div>
            </div>
            {privacy === AllowedPrivacy.PRIVATE && allowedPrivacies.length > 1 ? (
                <>
                    <div className="mb-2 flex items-center justify-between">
                        <Label>Private Pricing Available for These Schedules</Label>
                        <AddSchedulePopover
                            value={pricingGroup.schedules}
                            experience={experience}
                            onSubmit={handleScheduleSubmit}
                        />
                    </div>
                    <ScheduleList experience={experience} value={pricingGroup.schedules} />
                </>
            ) : null}
        </div>
    );
};

const ScheduleItem = ({ children }: { children: ReactNode }) => {
    return <div className="py-3 px-2">{children}</div>;
};

const ScheduleList = ({ experience, value }: { experience: Experience; value: PricingGroupSchedules }) => {
    const filteredSchedules = experience.schedules
        ? experience.schedules.filter((schedule) => schedule.id && (value.all || value.items.includes(schedule.id)))
        : [];

    return (
        <div className="max-h-40 divide-y divide-gray-lighter overflow-y-auto rounded border border-gray-lighter">
            {value.items.length === 0 ? (
                <ScheduleItem>
                    <span>
                        {/* TODO: use DoubleCheckIcon after it is added */}
                        <CheckIcon className="mr-1" />
                        {value.all ? "All" : "No"} schedules selected
                    </span>
                </ScheduleItem>
            ) : (
                filteredSchedules.map((schedule) => {
                    return (
                        <ScheduleItem key={schedule.id}>
                            <NoTranslate if={!!schedule.name}>
                                {schedule.name?.trim() ?? getScheduleSummary(schedule)}
                            </NoTranslate>
                        </ScheduleItem>
                    );
                })
            )}
        </div>
    );
};
